/**
 * Context captured automatically with every piece of feedback or support
 * request, so staff can tell *where in the theatre* something happened without
 * asking the visitor to explain the site's layout back to us.
 */

export interface FeatureArea {
  value: string;
  label: string;
}

/** Every named room of the theatre, in the order a visitor tends to meet them. */
export const FEATURE_AREAS: FeatureArea[] = [
  { value: "foyer", label: "Foyer (Tonight)" },
  { value: "discovery", label: "Discovery & Search" },
  { value: "vault", label: "Cinema Vault" },
  { value: "random", label: "Random Pick" },
  { value: "movie_night", label: "Movie Night" },
  { value: "watch_planner", label: "Watch Planner" },
  { value: "collections", label: "Collections & Archive" },
  { value: "community", label: "The Lobby (Community)" },
  { value: "messages", label: "Messages" },
  { value: "profile", label: "Profile & Activity" },
  { value: "vip", label: "VIP & Billing" },
  { value: "account", label: "Account & Settings" },
  { value: "notifications", label: "Notifications" },
  { value: "email", label: "Emails" },
  { value: "onboarding", label: "First Screening / Tour" },
  { value: "other", label: "Somewhere else" },
];

const ROUTE_AREAS: Array<[RegExp, string]> = [
  [/^\/movie-night/, "movie_night"],
  [/^\/watch-planner/, "watch_planner"],
  [/^\/collections?/, "collections"],
  [/^\/collection\//, "collections"],
  [/^\/community/, "community"],
  [/^\/messages/, "messages"],
  [/^\/profile/, "profile"],
  [/^\/u\//, "profile"],
  [/^\/vip/, "vip"],
  [/^\/account-settings/, "account"],
  [/^\/notifications/, "notifications"],
  [/^\/email-/, "email"],
  [/^\/support/, "other"],
  [/^\/admin/, "other"],
];

const TAB_AREAS: Record<string, string> = {
  tonight: "foyer",
  discover: "discovery",
  "ai-search": "discovery",
  search: "discovery",
  vault: "vault",
  random: "random",
  watchlist: "collections",
  premium: "vip",
  analyze: "discovery",
};

/** Best guess at which feature the visitor is looking at right now. */
export function detectFeatureArea(pathname = window.location.pathname, search = window.location.search): string {
  for (const [re, area] of ROUTE_AREAS) {
    if (re.test(pathname)) return area;
  }
  if (pathname === "/" || pathname === "/index") {
    const tab = new URLSearchParams(search).get("tab");
    if (tab && TAB_AREAS[tab]) return TAB_AREAS[tab];
    return "foyer";
  }
  return "other";
}

export function featureAreaLabel(value?: string | null): string {
  if (!value) return "Unknown";
  return FEATURE_AREAS.find((a) => a.value === value)?.label ?? value;
}

/** Current page path + query, trimmed to something safe to store. */
export function currentPagePath(): string {
  return `${window.location.pathname}${window.location.search}`.slice(0, 300);
}

export interface BrowserInfo {
  ua: string;
  viewport: string;
  dpr: number;
  language: string;
  platform?: string;
  timezone?: string;
  referrer?: string;
}

export function collectBrowserInfo(): BrowserInfo {
  let timezone: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = undefined;
  }
  return {
    ua: navigator.userAgent.slice(0, 300),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    dpr: Number(window.devicePixelRatio?.toFixed(2) ?? 1),
    language: navigator.language,
    platform: (navigator as Navigator & { platform?: string }).platform,
    timezone,
    referrer: document.referrer ? document.referrer.slice(0, 200) : undefined,
  };
}

/** Short human summary of a browser_info blob for the admin inbox. */
export function describeDevice(info: unknown): string {
  const i = (info ?? {}) as Partial<BrowserInfo>;
  if (!i.ua) return "Unknown device";
  const ua = i.ua;
  const os = /iPhone|iPad/.test(ua)
    ? "iOS"
    : /Android/.test(ua)
      ? "Android"
      : /Mac OS X/.test(ua)
        ? "macOS"
        : /Windows/.test(ua)
          ? "Windows"
          : /Linux/.test(ua)
            ? "Linux"
            : "Unknown OS";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\//.test(ua)
      ? "Opera"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : /Firefox\//.test(ua)
            ? "Firefox"
            : "Unknown browser";
  return [os, browser, i.viewport].filter(Boolean).join(" · ");
}

export const FEEDBACK_TYPES = [
  { value: "bug", label: "Something's broken", hint: "A button, page, or feature misbehaves" },
  { value: "feature", label: "Idea / request", hint: "Something you wish Swiftix had" },
  { value: "confusing", label: "Confusing UI", hint: "You couldn't figure out where to go" },
  { value: "performance", label: "Slow / laggy", hint: "Loading, stutter, or freezing" },
  { value: "content", label: "Wrong info", hint: "Bad title data, ratings, or providers" },
  { value: "praise", label: "Something you loved", hint: "Tell us what to keep doing" },
] as const;

export type FeedbackType = (typeof FEEDBACK_TYPES)[number]["value"];

export const SEVERITIES = [
  { value: "blocking", label: "Blocking — I can't continue" },
  { value: "annoying", label: "Annoying — there's a workaround" },
  { value: "minor", label: "Minor — cosmetic only" },
] as const;

export function feedbackTypeLabel(value?: string | null): string {
  if (!value) return "Feedback";
  return FEEDBACK_TYPES.find((t) => t.value === value)?.label ?? value;
}
